import { queryOptions, useQuery, type UseQueryResult } from '@tanstack/react-query'
import { COMMUNITY_API_PREFIX } from 'shared/api/constants/api-constants'
import type { PaginationData } from 'shared/components/pagination'
import { useApiWrapperWithErrorValidation } from 'shared/hooks/use-api-wrapper-with-error-validation'
import { RequestMethodsEnum } from 'shared/hooks/use-api-wrapper-with-error-validation/types'
import type { UseQueryClearOptions } from 'shared/types/tanstack-utility-types'
import { addQueryParam } from 'shared/utils/add-query-param'
import type { MemberInterface } from 'modules/community/types/member-interface'
import { communityKeys } from './key-factory'

const MEMBER_LIMIT = 9

interface UseMembersOptions extends Omit<
  UseQueryClearOptions<PaginationData<MemberInterface[]>, Error, PaginationData<MemberInterface[]>>,
  'enabled' | 'placeholderData'
> {
  communityPath: string
  page: number
  searchBy: string
}

export const useMembers = ({
  communityPath,
  page,
  searchBy,
  ...options
}: UseMembersOptions): UseQueryResult<PaginationData<MemberInterface[]>, Error> => {
  const { fetcher } = useApiWrapperWithErrorValidation<
    RequestMethodsEnum.get,
    PaginationData<MemberInterface[]>
  >({
    method: RequestMethodsEnum.get,
  })

  return useQuery(
    queryOptions({
      queryKey: communityKeys.members.list(communityPath, page, searchBy),
      queryFn: () => {
        const search = new URLSearchParams()
        addQueryParam(search, 'searchBy', searchBy)
        return fetcher(
          `${COMMUNITY_API_PREFIX}/${communityPath}/members/${page}/${MEMBER_LIMIT}?${search}`,
        )
      },
      enabled: Boolean(communityPath),
      ...options,
    }),
  )
}
